import { Box, Dialog, DialogContent, Typography } from '@mui/material';
import _ from 'lodash';
import React, { FC, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  generalSettings,
  moduleSettings,
} from '../../../../features/Settings/services';
import Setting from '../../../../features/Settings/interfaces/Setting';
import useNavigationLink from '../../../../hooks/useNavigationLink';
import useUser from '../../../../hooks/useUser';

import NavigationLink from '../../../../interfaces/NavigationLink';
import SearchNotFound from '../../../../shared/icons/SearchNotFound';
import CasaLogo from '../../../../shared/logo/CasaLogo';
import FuzzySearch from '../../utils/FuzzySearch';
import InstantSearchHeader from '../InstantSearchHeader/InstantSearchHeader';
import InstantSearchList from '../InstantSearchList/InstantSearchList';
import styles from './InstantSearchDialog.module.scss';

interface InstantSearchDialogProps {
  onClose: () => void;
}

const InstantSearchDialog: FC<InstantSearchDialogProps> = ({ onClose }) => {
  const [enteredSearchTerm, setEnteredSearchTerm] = useState('');
  const [searchResult, setSearchResult] = useState<NavigationLink[]>([]);
  const { navigationLinks } = useNavigationLink();
  const { userPermissions } = useUser();
  const tNav = useTranslations('nav');
  const tSettings = useTranslations('settings');

  const combinedLinks = useMemo(() => {
    const translateLink = (link: NavigationLink): NavigationLink => ({
      ...link,
      label: tNav.has(link.label) ? tNav(link.label) : link.label,
      content: _.isString(link.content)
        ? link.content
        : link.content.map(translateLink),
    });

    const mapSettings = (settings: Setting[]) =>
      settings
        .filter((setting) =>
          _.isEmpty(_.difference(setting.permissions, userPermissions))
        )
        .map(
          (setting): NavigationLink => ({
            label: tSettings(`items.${setting.id}.label`),
            content: setting.path,
            requiredPermissions: setting.permissions,
            externalLink: setting.external,
            icon: setting.icon,
          })
        );

    return [
      ...navigationLinks
        .filter((link) => !link.locked && !link.comingSoon)
        .map(translateLink),
      {
        label: tSettings('tabs.general'),
        requiredPermissions: [],
        externalLink: false,
        content: mapSettings(generalSettings),
      },
      {
        label: tSettings('tabs.modules'),
        requiredPermissions: [],
        externalLink: false,
        content: mapSettings(moduleSettings),
      },
    ];
  }, [navigationLinks, userPermissions, tNav, tSettings]);

  const fuzzySearch = useMemo(
    () => new FuzzySearch(combinedLinks),
    [combinedLinks]
  );

  useLayoutEffect(() => {
    setSearchResult(fuzzySearch.query(enteredSearchTerm));
  }, [enteredSearchTerm, fuzzySearch]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ sx: { height: '75%' } }}
      id={styles.instantSearchDialog}
    >
      <InstantSearchHeader
        searchTerm={enteredSearchTerm}
        onSearchTermChange={setEnteredSearchTerm}
        onClose={onClose}
      />
      <DialogContent dividers sx={{ py: 1, px: 2.5 }}>
        <InstantSearchList
          list={searchResult}
          isParentElement
          onClose={onClose}
        />
        {enteredSearchTerm && searchResult.length === 0 && (
          <Box className={styles.noResultFound}>
            <SearchNotFound fontSize='large' color='disabled' />
            <Typography>
              No results for "<strong>{enteredSearchTerm}</strong>"
            </Typography>
          </Box>
        )}
      </DialogContent>
      <Box component='footer' className={styles.instantSearchFooter}>
        <CasaLogo width={60} />
      </Box>
    </Dialog>
  );
};

export default InstantSearchDialog;
